import { mdiMagnify } from "@mdi/js";
import Icon from "@mdi/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import DataTable from "components/DataTable";
import Input from "components/Input";
import { RefLoadingSpinner } from "components/LoadingSpinner";
import Select from "components/Select";
import {
  MY_CARRIERS_TABLE_HEADER_LIST,
  ORIGIN_CARRIER_SEARCH_FILTER_LIST,
  ORIGIN_CARRIER_STATUS_FILTER_LIST,
  PERCENTAGE_LIST
} from "constants/haulpay-broker";
import { routes } from "constants/routes";
import MyCarrierItem from "features/main/HaulPay/modules/Broker/MyCarrierItem";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useUserFeature } from "hooks/useUserFeature";
import { IOption } from "models/Form";
import { IFactoringPaymentProfile, IMyCarrierFilterOption } from "models/HaulPay";
import { getFactoringPaymentProfilesAPI, updateCarrierProfileAPI } from "services/api/haulpay";
import { selectFactoringProfile, selectUserDetails } from "store/user/selector";
import CarrierInfoModal from "./CarrierInfoModal";
import DefaultSettingsModal from "./DefaultSettingModal";
import { buildPaymentSpeeds } from "./paymentSpeeds";
import styles from "./style.module.scss";
import { useBrokerContext } from "context/BrokerContext";
import { usePullToRefresh } from "context/PullToRefreshContext";

export interface ICarriersFormData {
  carrier_id: string;
  carrier_rate: string[];
  external_code: string[];
  payment_speed: (IOption & {
    amount: number;
    discountRate?: number;
    rate?: number;
  })[];
  broker_portion: IOption[];
}

export default function MyCarriers() {
  const navigate = useNavigate();
  const userDetails = useSelector(selectUserDetails);
  const factoringProfile = useSelector(selectFactoringProfile);
  const { isDesktop } = useDeviceType();
  const { showExternalCode } = useUserFeature();

  const [selectedCarrier, setSelectedCarrier] = useState<IFactoringPaymentProfile>();
  const [savingCarrierId, setSavingCarrierId] = useState<string | null>(null);

  const [isDefaultSettingModalOpen, toggleDefaultSettingModalOpen] = useToggle(false);
  const [isCarrierInfoModalOpen, toggleCarrierInfoModal] = useToggle();
  const { defaultPaymentSettings, getDefaultPaymentSettings } = useBrokerContext();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { isSubmitting }
  } = useForm<IMyCarrierFilterOption>({
    defaultValues: {
      filter_text: "",
      filter_type: "carrier_dba",
      filter_status: "pending"
    }
  });

  const fetchMyCarriers = useCallback(
    async (factoringId: string, params: any) => {
      const filterData = getValues();
      return (
        factoringId &&
        getFactoringPaymentProfilesAPI(factoringId, filterData, params.limit, params.offset)
      );
    },
    [userDetails?.factoring_id]
  );

  const {
    data: myCarriers,
    loading: isLoadingMore,
    paginationInfo,
    observerRef,
    goToPage,
    loadNextPage,
    loadPreviousPage,
    setPageSizeFn,
    resetPagination,
    fetch: fetchCarriersPage
  } = useInfiniteScroll<IFactoringPaymentProfile>(
    (params: any) => fetchMyCarriers(userDetails.factoring_id, params),
    {
      threshold: 0.9,
      concatData: true
    },
    [userDetails?.factoring_id]
  );

  const refreshMyCarriers = () =>
    Promise.all([
      fetchCarriersPage({ offset: 0, add: false }),
      getDefaultPaymentSettings(userDetails.factoring_id)
    ]);
  usePullToRefresh(refreshMyCarriers, { enabled: Boolean(userDetails?.factoring_id) });

  const methods = useForm<Partial<ICarriersFormData>>();
  const { getValues: getCarriersListValue, reset: resetCarriersForm } = methods;

  const filterType = watch("filter_type");

  function goToAddNew() {
    navigate(routes.HAULPAY_BROKER_CARRIERS_NEW);
  }

  const onFilterCarriers = async () => {
    await resetPagination();
  };

  const setMyCarriersData = (carriers: IFactoringPaymentProfile[]) => {
    resetCarriersForm({
      external_code: carriers.map((carrier) => carrier.external_code),
      payment_speed: carriers.map((carrier) => {
        const paymentTerms = buildPaymentSpeeds(carrier.payment_speed);
        return paymentTerms.find((speed) => speed.value === carrier.payout_days);
      }),
      broker_portion: carriers.map((carrier) =>
        PERCENTAGE_LIST.find(
          (percent) => Number(percent.value) === carrier.factoring_fee_split_rate
        )
      ),
      carrier_rate: carriers.map((carrier) => {
        const paymentTerm = carrier?.payment_speed?.find((term) => {
          const payoutDays = term.payout_days === 0 ? 1 : term.payout_days;
          return payoutDays == Number(carrier.payout_days?.match(/-?\d+(\.\d+)?/g)?.[0]);
        });
        if (paymentTerm) {
          return (
            (paymentTerm?.discount_rate * (100 - carrier?.factoring_fee_split_rate)) / 100 + "%"
          );
        }
        return "0%";
      })
    });
  };

  const onSaveCarrier = async (index: number, id: string) => {
    const info = getCarriersListValue([
      `payment_speed.${index}`,
      `broker_portion.${index}`,
      `carrier_rate.${index}`,
      `external_code.${index}`
    ]);
    try {
      setSavingCarrierId(id);
      await updateCarrierProfileAPI({
        factoring_id: userDetails.factoring_id,
        carrier_id: id,
        send_update_email: factoringProfile.send_rate_confirmation_pdf,
        data: {
          enable_discount: myCarriers.find((carrier) => carrier.id === id)?.enable_discount,
          external_code: info[3],
          factoring_fee_split_rate: Number(info[1].value),
          payout_days: info[0].value
        }
      });
      showSuccessMessage("Carrier has been updated.");
    } catch (err: any) {
      showErrorMessage(err.message);
    } finally {
      setSavingCarrierId(null);
    }
  };

  const handleToggleInfoModal = (id: string) => {
    setSelectedCarrier(myCarriers.find((carrier) => carrier.id === id));
    toggleCarrierInfoModal();
  };

  const pendingTasks = (
    needs_account_info: boolean,
    needs_factor_info: boolean,
    needs_noa: boolean,
    needs_verified_identity: boolean
  ) => {
    const tasks = [];
    if (needs_account_info) {
      tasks.push("Need Acct. info");
    }
    if (needs_factor_info) {
      tasks.push("Need Factor info");
    }
    if (needs_noa) {
      tasks.push("Need NOA");
    }
    if (needs_verified_identity) {
      tasks.push("Verifying Bank Acct or NOA");
    }
    return tasks;
  };

  useEffect(() => {
    setMyCarriersData(myCarriers);
  }, [myCarriers]);

  useEffect(() => {
    if (userDetails?.factoring_id && !defaultPaymentSettings.length) {
      getDefaultPaymentSettings(userDetails?.factoring_id);
    }
  }, [userDetails?.factoring_id, defaultPaymentSettings.length]);

  const columns = useMemo(
    () =>
      MY_CARRIERS_TABLE_HEADER_LIST(
        myCarriers,
        onSaveCarrier,
        handleToggleInfoModal,
        defaultPaymentSettings,
        methods.register,
        methods.control,
        methods.getValues,
        methods.setValue,
        savingCarrierId
      ).filter((column) => showExternalCode || column.value !== "external_code"),
    [defaultPaymentSettings, myCarriers, savingCarrierId, showExternalCode]
  );

  return (
    <div className={styles.originCarrierContainer}>
      <div className={styles.body}>
        <form
          className="flex desktop:w-2/3 gap-2 align-center mobile:flex-col"
          onSubmit={handleSubmit(onFilterCarriers)}>
          <Input
            name="filter_text"
            prefixNode={<Icon path={mdiMagnify} className={clsx(styles.searchButton)} />}
            placeholder="Search for"
            register={register}
            autoComplete="off"
            className="mobile:w-full"
          />
          <div className="flex gap-2 flex-row">
            <Controller
              name="filter_type"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  options={ORIGIN_CARRIER_SEARCH_FILTER_LIST}
                  value={ORIGIN_CARRIER_SEARCH_FILTER_LIST.find(
                    (filter_type) => filter_type.value === value
                  )}
                  onChange={(item) => {
                    onChange(item);
                    setValue("filter_type", item.value);
                  }}
                  btnClass="!py-4 !pl-3 desktop:!pr-12 !rounded-lg text-sm"
                  dropdownClass="h-auto"
                  dropdownContainerClass="max-h-auto"
                  className="w-fit desktop:w-40"
                  arrowClass="!right-1"
                />
              )}
            />

            {filterType === "status" && (
              <Controller
                name="filter_status"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={ORIGIN_CARRIER_STATUS_FILTER_LIST}
                    value={ORIGIN_CARRIER_STATUS_FILTER_LIST.find(
                      (filter_status) => filter_status.value === value
                    )}
                    onChange={(item) => {
                      onChange(item);
                      setValue("filter_status", item.value);
                    }}
                    btnClass="!py-4 !pl-3 desktop:!pr-12 !rounded-lg text-sm"
                    dropdownClass="h-auto"
                    dropdownContainerClass="max-h-auto"
                    className="w-full desktop:w-40"
                    arrowClass="!right-1"
                  />
                )}
              />
            )}
            <Button
              type="submit"
              variant="outline"
              className="!h-12 mobile:text-xs desktop:!px-12 mobile:!px-6 desktop:!rounded-base"
              loading={isSubmitting}>
              Search
            </Button>
          </div>
        </form>

        <div className="flex desktop:w-1/3 gap-2 mobile:w-full justify-end">
          <Button
            variant="outline"
            className="desktop:!h-12 desktop:!px-9 desktop:!rounded-base desktop:order-1 order-2"
            onClick={toggleDefaultSettingModalOpen}>
            Defaults
          </Button>
          <Button
            className="desktop:!h-12 mobile:text-xs desktop:!px-9 !px-5 desktop:!rounded-base desktop:order-2 order-1"
            onClick={goToAddNew}>
            {isDesktop ? "Add New" : "ADD NEW"}
          </Button>
        </div>
      </div>

      {isDesktop ? (
        <form>
          <DataTable
            className={styles.desktopCarrierList}
            headers={columns || []}
            data={myCarriers.map((item) => ({
              ...item,
              status: {
                carrierStatus: item.payment_profile.status,
                statusDate: item.status_date,
                pendingApprovalTasks: pendingTasks(
                  item.payment_profile.needs_account_info,
                  item.payment_profile.needs_factor_info,
                  item.payment_profile.needs_noa,
                  item.payment_profile.needs_verified_identity
                )
              }
            }))}
            totalCount={paginationInfo.totalCount}
            nextLink={paginationInfo.hasNextPage}
            prevLink={paginationInfo.hasPreviousPage}
            setPageSize={setPageSizeFn}
            goToPage={goToPage}
            fetchPrevPage={loadPreviousPage}
            fetchNextPage={loadNextPage}
            loading={isLoadingMore || isSubmitting}
            infiniteScroll
          />
        </form>
      ) : (
        <div className={styles.carrierList}>
          {myCarriers.map((item) => (
            <MyCarrierItem
              key={item.id}
              data={item}
              send_update_email={factoringProfile.send_rate_confirmation_pdf}
              toggleInfoModal={handleToggleInfoModal}
              pendingApprovalTasks={pendingTasks(
                item.payment_profile.needs_account_info,
                item.payment_profile.needs_factor_info,
                item.payment_profile.needs_noa,
                item.payment_profile.needs_verified_identity
              )}
              defaultPaymentSettings={defaultPaymentSettings}
            />
          ))}
          <RefLoadingSpinner observerRef={observerRef} isLoadingMore={isLoadingMore} />
        </div>
      )}
      {isDefaultSettingModalOpen && (
        <DefaultSettingsModal
          isOpen={isDefaultSettingModalOpen}
          closeModal={toggleDefaultSettingModalOpen}
        />
      )}

      {myCarriers.length ? (
        <CarrierInfoModal
          isOpen={isCarrierInfoModalOpen}
          closeModal={() => toggleCarrierInfoModal()}
          carrierInfo={selectedCarrier ?? myCarriers[0]}
        />
      ) : null}
    </div>
  );
}
